import React from 'react';

import { IntlProvider } from 'react-intl';

import { getDocument } from '@atlaskit/browser-apis';
import type { PortalProviderAPI } from '@atlaskit/editor-common/portal';
import type { ExtractInjectionAPI, PMPluginFactoryParams } from '@atlaskit/editor-common/types';
import type { Node as PMNode } from '@atlaskit/editor-prosemirror/model';
import { Decoration } from '@atlaskit/editor-prosemirror/view';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import { expValEqualsNoExposure } from '@atlaskit/tmp-editor-statsig/exp-val-equals-no-exposure';
import { RemixThinkingBar } from '@atlassian/malleable-ui-components/remix/remix-thinking-bar';

import { stopRemixThinking } from '../../editor-commands/stop-remix-thinking';
import type { MalleableUiPlugin } from '../../malleableUiPluginType';
import {
	cancelRemixThinking,
	getAbortRequest,
	onRemixThinkingCompleteAnimationFinished,
} from '../../ui/utils/remix-state';

export const THINKING_WIDGET_KEY = 'remix-thinking-widget';
const THINKING_WIDGET_PLACEHOLDER_KEY = 'remix-thinking-widget-placeholder';

export function renderRemixThinkingPortal({
	api,
	container,
	contentType,
	getIntl,
	isComplete,
	remixAttemptId,
	nodeViewPortalProviderAPI,
}: {
	api?: ExtractInjectionAPI<MalleableUiPlugin>;
	container: HTMLElement;
	contentType?: string;
	getIntl: PMPluginFactoryParams['getIntl'];
	isComplete: boolean;
	nodeViewPortalProviderAPI: PortalProviderAPI;
	remixAttemptId?: string;
}): void {
	const intl = getIntl();

	const remixModalMetadata = api?.malleableUi?.sharedState.currentState()?.remixModalMetadata;
	const userPrompt = remixModalMetadata?.userPrompt;

	const onStopThinking = () => {
		const shouldCloseModal =
			!userPrompt && expValEquals('cc_maui_polish_changes_batch_2', 'isEnabled', true);
		if (fg('platform_editor_ai_remix_toolbar')) {
			// Abort before cancelling so send-message-action.ts fires the paired
			// aiInteraction initiated + dismissed events via AbortError.
			getAbortRequest(api)?.();
			cancelRemixThinking({ api, reopenModal: shouldCloseModal ? false : undefined });
		} else {
			api?.aiExperience?.actions.toggleAiEntryPoints?.(false);

			if (
				expValEqualsNoExposure('cc_page_experiences_editor_image_generation', 'isEnabled', true)
			) {
				const onCancel = remixModalMetadata?.onCancel;
				if (onCancel && userPrompt) {
					// Abort the AI request so send-message-action.ts fires the paired
					// aiInteraction initiated + dismissed events automatically via AbortError.
					getAbortRequest(api)?.();
					api?.core.actions.execute(stopRemixThinking({ reopenModal: false }));
					onCancel();
					return;
				}
			}

			const isUpdate = api?.malleableUi?.sharedState.currentState()?.isUpdate ?? false;

			api?.core.actions.execute(
				stopRemixThinking({
					reopenModal: shouldCloseModal
						? false
						: fg('platform_editor_ai_remix_toolbar')
							? !isUpdate
							: true,
				}),
			);
		}
	};

	const onComplete = () => {
		if (fg('platform_editor_ai_remix_toolbar')) {
			onRemixThinkingCompleteAnimationFinished({ api });
		} else {
			api?.aiExperience?.actions.toggleAiEntryPoints?.(false);
			api?.core.actions.execute(stopRemixThinking());
		}
	};

	nodeViewPortalProviderAPI.render(
		() => (
			<IntlProvider
				defaultLocale={intl.defaultLocale || 'en-US'}
				locale={intl.locale || 'en-US'}
				messages={intl.messages}
				formats={intl.formats}
				onError={intl.onError}
			>
				<RemixThinkingBar
					remixAttemptId={remixAttemptId}
					loadingBarType="big-bar"
					// when userPrompt is not set, it means it is a remix entrance
					// otherwise it is a non-remix entrance
					isRemixEntrance={
						expValEqualsNoExposure('cc_page_experiences_editor_image_generation', 'isEnabled', true)
							? !userPrompt
							: true
					}
					contentType={contentType}
					isComplete={isComplete}
					isRelative
					onCompleteAnimationFinished={onComplete}
					onStop={onStopThinking}
					showProgressBar
				/>
			</IntlProvider>
		),
		container,
		THINKING_WIDGET_KEY,
		undefined,
		true,
	);
}

function createRemixThinkingWidget({
	api,
	contentType,
	getIntl,
	isComplete,
	isPlaceholder,
	remixAttemptId,
	nodeViewPortalProviderAPI,
}: {
	api?: ExtractInjectionAPI<MalleableUiPlugin>;
	contentType?: string;
	getIntl: PMPluginFactoryParams['getIntl'];
	isComplete: boolean;
	/**
	 * after the remix thinking is complete, we need to scroll to the widget if it is not in the viewport
	 * so we need to use the widget as a anchor point to scroll to
	 * but we don't need to render the content in the widget
	 */
	isPlaceholder: boolean;
	nodeViewPortalProviderAPI: PortalProviderAPI;
	remixAttemptId?: string;
}): HTMLElement {
	const document = getDocument();
	if (!document) {
		throw new Error('Document is required to render the remix thinking widget.');
	}

	const container = document.createElement('div');
	container.className = isPlaceholder
		? `${THINKING_WIDGET_KEY} ${THINKING_WIDGET_PLACEHOLDER_KEY}`
		: THINKING_WIDGET_KEY;
	container.dataset.testid = isPlaceholder ? THINKING_WIDGET_PLACEHOLDER_KEY : THINKING_WIDGET_KEY;

	if (!isPlaceholder) {
		renderRemixThinkingPortal({
			api,
			container,
			contentType,
			getIntl,
			isComplete,
			remixAttemptId,
			nodeViewPortalProviderAPI,
		});
	}

	return container;
}

export function getRemixThinkingWidgetContainer(view: EditorView): HTMLElement | null {
	return view.dom.querySelector<HTMLElement>(`.${THINKING_WIDGET_KEY}`);
}

function getPositionAfterDepthOneParent({ doc, to }: { doc: PMNode; to: number }): number {
	const $to = doc.resolve(to);

	return $to.depth >= 1 ? $to.after(1) : to;
}

export function createRemixThinkingWidgetDecoration({
	api,
	contentType,
	doc,
	getIntl,
	isComplete,
	isPlaceholder,
	remixAttemptId,
	nodeViewPortalProviderAPI,
	to,
}: {
	api?: ExtractInjectionAPI<MalleableUiPlugin>;
	contentType?: string;
	doc: PMNode;
	getIntl: PMPluginFactoryParams['getIntl'];
	isComplete?: boolean;
	/**
	 * when the remix thinking isComplete or isUpdate, we need to render the widget as a placeholder
	 * we don't need to render the real content in the widget
	 * we just use it as an anchor point to
	 * - check if the widget is in the viewport and if not show the ready toast
	 * - scroll to the widget to see the content
	 */
	isPlaceholder: boolean;
	nodeViewPortalProviderAPI: PortalProviderAPI;
	remixAttemptId?: string;
	to: number;
}): Decoration {
	const decorationKey = isPlaceholder ? THINKING_WIDGET_PLACEHOLDER_KEY : THINKING_WIDGET_KEY;
	const widgetPosition = getPositionAfterDepthOneParent({ doc, to });

	return Decoration.widget(
		widgetPosition,
		() =>
			createRemixThinkingWidget({
				api,
				contentType,
				getIntl,
				isComplete: isComplete ?? false,
				isPlaceholder,
				remixAttemptId,
				nodeViewPortalProviderAPI,
			}),
		{
			key: decorationKey,
			// use a low negative side to ensure the decoration is rendered before all other decorations
			// so other decorations update don't cause this decoration to be re-rendered
			side: -999,
			destroy: () => {
				if (!isPlaceholder) {
					nodeViewPortalProviderAPI.remove(THINKING_WIDGET_KEY);
				}
			},
		},
	);
}
